import type { WsSmmState } from './slice';

export interface SmmWsStore {
  wsSmm: WsSmmState;
}

export const selectWsSmmStatus = (store: SmmWsStore) => store.wsSmm.status;
export const selectWsSmmError = (store: SmmWsStore) => store.wsSmm.error;
export const selectIsWsSmmReconnecting = (store: SmmWsStore) => store.wsSmm.isReconnecting;

/**
 * How often a PUBLISHING post is read again while the SMM socket is down.
 * Once it is connected its `Invalidate` frames refetch the post, so polling
 * does not stop but slows to
 * {@link SMM_PUBLISHING_SAFETY_POLLING_INTERVAL_MS}.
 */
export const SMM_PUBLISHING_POLLING_INTERVAL_MS = 5_000;

/**
 * How often a PUBLISHING post is still read again while the SMM socket is
 * connected. A connected socket is an open one, not necessarily an
 * authenticated one (a refused `Auth` closes it only afterwards, and the
 * gateway sends no acknowledgement to wait for), and frames sent while it
 * was flapping are lost: this bounds how long a post can look stuck.
 *
 * TODO(SKY-4868): drop this poll for authenticated sockets once assistant-smm
 * is confirmed to acknowledge `Auth` (`{ command: 'ok', okCommand: 'Auth' }`,
 * option 1 of the ticket) and SKY-4867 catches the missed frames up after a
 * reconnect. Until both hold, an open socket is no proof a frame will arrive.
 */
export const SMM_PUBLISHING_SAFETY_POLLING_INTERVAL_MS = 30_000;

/**
 * Whether the SMM socket is open (not necessarily authenticated). Polling
 * fallbacks (e.g. PUBLISHING status, every
 * {@link SMM_PUBLISHING_POLLING_INTERVAL_MS}) run at full rate while this is
 * `false`, and slow down to a safety poll while it is `true`.
 *
 * Takes a store that may lack the `wsSmm` slice: a host that never registers
 * the channel has no socket, so it reads as disconnected and the fallbacks poll.
 */
export const selectWsSmmConnected = (store: Partial<SmmWsStore>) =>
  store.wsSmm?.status === 'connected';
